import axios from "axios";
import { generateProvisionalInvoiceNumber } from "../utils/manualPayment";

function getApiBase() {
  return (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "") + "/";
}

/**
 * Create / register a manual bank-transfer payment with an invoice number.
 *
 * Expected backend (see docs/backend-manual-payment-prompt.md):
 * POST Payment/CreateManualBankPayment
 * Body: { parcelId, recordSerialNo, orderAmount, currencyTypeId, bookedById, description? }
 * Response: { invoiceNumber | paymentInvoiceNo, paymentId?, paymentStatusId? }
 *
 * Falls back to a client-generated invoice if the API is not ready yet.
 */
export async function createManualBankPaymentInvoice(payload) {
  const {
    parcelId,
    recordSerialNo,
    orderAmount,
    currencyTypeId,
    bookedById,
    description,
  } = payload || {};

  if (!parcelId && !recordSerialNo) {
    throw new Error("parcelId or recordSerialNo is required for manual payment invoice");
  }

  try {
    const { data } = await axios.post(
      `${getApiBase()}Payment/CreateManualBankPayment`,
      {
        parcelId,
        recordSerialNo,
        orderAmount,
        currencyTypeId,
        bookedById,
        description: description || "Manual bank transfer payment",
        paymentTypeMethodId: 5,
      },
    );

    const invoiceNumber =
      data?.invoiceNumber ||
      data?.paymentInvoiceNo ||
      data?.invoiceNo ||
      null;

    if (invoiceNumber) {
      return {
        invoiceNumber: String(invoiceNumber),
        paymentId: data?.paymentId ?? data?.id ?? null,
        paymentStatusId: data?.paymentStatusId ?? 1,
        source: "api",
        raw: data,
      };
    }
  } catch (err) {
    // Backend endpoint may not exist yet — fall through to provisional invoice.
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "[ManualPayment] CreateManualBankPayment unavailable, using provisional invoice",
        err?.response?.status || err?.message,
      );
    }
  }

  const invoiceNumber = generateProvisionalInvoiceNumber();
  return {
    invoiceNumber,
    paymentId: null,
    paymentStatusId: 1,
    source: "provisional",
    raw: null,
  };
}
